export interface PaymentPricingSource {
  primaryPrice?: unknown;
  depositFee?: unknown;
  depoistFee?: unknown;
  deposit?: unknown;
  [key: string]: unknown;
}

export type GpBookingOption = 'consultation' | 'pathology-review';

export type CardiologyBookingOption = 'consultation' | 'bp-monitoring';

export const CARDIOLOGY_BP_MONITORING_PRICE = 495;
export const CARDIOLOGY_BP_MONITORING_NOTE = '24-hour BP Monitoring price £495';

export interface PaymentPricingOptions {
  selectedDoctor?: PaymentPricingSource;
  selectedClinic?: PaymentPricingSource;
  paymentDetails?: PaymentPricingSource;
  paymentChoice?: 'full' | 'deposit';
  // Package-level overrides from store. When set, these take precedence over
  // selectedDoctor/selectedClinic fields for package-originated bookings.
  packagePrice?: number | null;
  storeDepositFee?: number | null;
  // GP booking type (categoryId '58'). 'pathology-review' forces total to £95.
  gpBookingOption?: GpBookingOption | null;
  cardiologyBookingOption?: CardiologyBookingOption | null;
}

export type PaymentPricingResult =
  | {
      hasDeposit: false;
      totalAmount: number;
      depositAmount: null;
      balanceDue: null;
      chargeAmountToday: number;
      buttonLabel: string;
    }
  | {
      hasDeposit: true;
      totalAmount: number;
      depositAmount: number;
      balanceDue: number;
      chargeAmountToday: number;
      buttonLabel: string;
    };

export function parsePrice(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(value, 0) : 0;
  }

  if (typeof value !== 'string') return 0;

  const parsed = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
}

export function formatPaymentAmount(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

export function getPaymentPricing({
  selectedDoctor,
  selectedClinic,
  paymentDetails,
  paymentChoice = 'deposit',
  packagePrice = null,
  storeDepositFee = null,
  gpBookingOption = null,
  cardiologyBookingOption = null,
}: PaymentPricingOptions = {}): PaymentPricingResult {
  const isPackageBooking = packagePrice !== null && packagePrice > 0;

  // totalAmount: packagePrice takes precedence when from a package booking
  const totalAmount = isPackageBooking
    ? packagePrice
    : cardiologyBookingOption === 'bp-monitoring' &&
        selectedDoctor?.categoryId === '33'
      ? CARDIOLOGY_BP_MONITORING_PRICE
      : gpBookingOption === 'pathology-review' &&
          selectedDoctor?.categoryId === '58'
        ? 95
        : parsePrice(
            selectedClinic?.primaryPrice ??
              selectedDoctor?.primaryPrice ??
              paymentDetails?.primaryPrice,
          );

  // depositAmount: storeDepositFee takes precedence when from a package booking
  const depositAmount =
    storeDepositFee !== null && storeDepositFee > 0
      ? storeDepositFee
      : parsePrice(
          selectedClinic?.depositFee ??
            selectedClinic?.depoistFee ??
            paymentDetails?.depositFee ??
            paymentDetails?.depoistFee ??
            selectedDoctor?.depositFee ??
            selectedDoctor?.depoistFee ??
            selectedDoctor?.deposit,
        );

  // Deposit-only doctor: no primaryPrice, deposit is the full amount
  const effectiveTotal = totalAmount > 0 ? totalAmount : depositAmount;

  const hasDeposit = depositAmount > 0;

  if (!hasDeposit) {
    return {
      hasDeposit: false,
      totalAmount: effectiveTotal,
      depositAmount: null,
      balanceDue: null,
      chargeAmountToday: effectiveTotal,
      buttonLabel:
        effectiveTotal === 0
          ? 'Confirm booking'
          : `Pay £${formatPaymentAmount(effectiveTotal)}`,
    };
  }

  const balanceDue = Math.max(effectiveTotal - depositAmount, 0);

  const chargeToday = paymentChoice === 'full' ? effectiveTotal : depositAmount;

  const label =
    paymentChoice === 'full'
      ? `Pay £${formatPaymentAmount(effectiveTotal)}`
      : `Pay Deposit £${formatPaymentAmount(depositAmount)}`;

  return {
    hasDeposit: true,
    totalAmount: effectiveTotal,
    depositAmount,
    balanceDue,
    chargeAmountToday: chargeToday,
    buttonLabel: label,
  };
}
